import includes from "lodash/includes";
import {some, every} from "lodash";
import { Component } from "react";
import Roles from "@app/user/roles";
import { UserInfo, UserContext } from "@app/user/UserContext";

/** Defines the set of roles to restrict a child component to. */
export interface RoleRestrictedProps {
  allowedRoles?: Roles[];
  deniedRoles?: Roles[];
  requiredRoles?: Roles[];
}

/** Wraps a child component, only rendering when the current user has the correct roles. */
export default class RoleRestricted extends Component<React.PropsWithChildren<RoleRestrictedProps>> {
  /** Defines the basic case for when no props are provided. */
  public static defaultProps: RoleRestrictedProps = {
    allowedRoles: [],
    deniedRoles: [],
    requiredRoles: [],
  };

  /**
   * Determines whether the user has the correct roles so that the child component can render.
   * If the user has any denied roles, then it is an instant fail.
   * @param user The user whose roles are checked.
   */
  public canRender(user: UserInfo): boolean {
    if (!user) {
      return false;
    }
    const hasAllowed = some(user.roles, (role) => includes(this.props.allowedRoles, role));
    const hasDenied = some(user.roles, (role) => includes(this.props.deniedRoles, role));
    const hasRequired = every(this.props.requiredRoles, (role) => includes(user.roles, role));

    return !hasDenied && (hasAllowed || this.props.allowedRoles.length === 0) && (hasRequired || this.props.requiredRoles.length === 0);
  }

  public render() {
    return (
      <UserContext.Consumer>
        {(user) => this.canRender(user)
          ? this.props.children
          : null
        }
      </UserContext.Consumer>
    );
  }
}
